import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { 
  Loader2, 
  Briefcase, 
  Calendar, 
  Clock,
  DollarSign,
  CheckCircle,
  XCircle,
  AlertTriangle,
  User,
  Phone,
  Mail,
  MapPin,
  CreditCard,
  FileText,
  Home,
  LogOut
} from 'lucide-react';
import { cn } from '@/lib/utils';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status configs
const OPERATIONAL_STATUSES = {
  new: { label: 'New', labelRu: 'Новый', color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300', icon: Clock },
  confirmed: { label: 'Confirmed', labelRu: 'Подтверждён', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: CheckCircle },
  in_progress: { label: 'In Progress', labelRu: 'В работе', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: Clock },
  done: { label: 'Completed', labelRu: 'Выполнен', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle },
  cancelled: { label: 'Cancelled', labelRu: 'Отменён', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: XCircle },
};

const PAYMENT_STATUSES = {
  unpaid: { label: 'Unpaid', labelRu: 'Не оплачен', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  partial: { label: 'Partial', labelRu: 'Частично', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  paid: { label: 'Paid', labelRu: 'Оплачен', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  refund: { label: 'Refund', labelRu: 'Возврат', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
};

function formatDate(dateStr) {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
}

function formatCurrency(amount) {
  return `$${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
}

// Invalid Token Component
function InvalidToken({ lang }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
            <XCircle className="h-6 w-6 text-red-600" />
          </div>
          <CardTitle>{lang === 'ru' ? 'Недействительная ссылка' : 'Invalid Link'}</CardTitle>
          <CardDescription>
            {lang === 'ru' 
              ? 'Ссылка на портал недействительна или истекла. Пожалуйста, запросите новую ссылку у вашего менеджера.'
              : 'This portal link is invalid or has expired. Please request a new link from your service provider.'}
          </CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
}

// Order Detail Sheet
function OrderDetailSheet({ order, open, onOpenChange, lang }) {
  if (!order) return null;

  const opStatus = OPERATIONAL_STATUSES[order.operational_status];
  const payStatus = PAYMENT_STATUSES[order.payment_status];
  const StatusIcon = opStatus?.icon || Clock;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            {order.title}
          </SheetTitle>
          <SheetDescription>
            {lang === 'ru' ? 'Детали заказа' : 'Order Details'}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Statuses */}
          <div className="flex flex-wrap gap-2">
            <Badge className={cn("flex items-center gap-1", opStatus?.color)}>
              <StatusIcon className="h-3 w-3" />
              {lang === 'ru' ? opStatus?.labelRu : opStatus?.label}
            </Badge>
            <Badge className={payStatus?.color}>
              {lang === 'ru' ? payStatus?.labelRu : payStatus?.label}
            </Badge>
          </div>

          {/* Schedule */}
          {order.scheduled_date && (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                <Calendar className="h-4 w-4 text-accent" />
              </div>
              <div>
                <p className="font-medium">{formatDate(order.scheduled_date)}</p>
                {order.scheduled_time_start && (
                  <p className="text-sm text-muted-foreground">
                    {order.scheduled_time_start} — {order.scheduled_time_end || '?'}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Address */}
          {(order.address_street || order.address_string) && (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                <MapPin className="h-4 w-4 text-accent" />
              </div>
              <div>
                <p className="font-medium">{order.address_street || order.address_string}</p>
                <p className="text-xs text-muted-foreground">
                  {lang === 'ru' ? 'Адрес' : 'Service Address'}
                </p>
              </div>
            </div>
          )}

          {/* Service Type */}
          {order.service_type_name && (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                <FileText className="h-4 w-4 text-accent" />
              </div>
              <div>
                <p className="font-medium">{order.service_type_name}</p>
                <p className="text-xs text-muted-foreground">
                  {lang === 'ru' ? 'Услуга' : 'Service Type'}
                </p>
              </div>
            </div>
          )}

          {/* Price */}
          <div className="p-4 rounded-lg bg-muted/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">
                {lang === 'ru' ? 'Сумма заказа' : 'Order Total'}
              </span>
              <span className="text-xl font-bold tabular-nums">
                {formatCurrency(order.total_amount)}
              </span>
            </div>
            {order.amount_paid > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {lang === 'ru' ? 'Оплачено' : 'Paid'}
                </span>
                <span className="text-green-600 font-medium">
                  {formatCurrency(order.amount_paid)}
                </span>
              </div>
            )}
            {order.total_amount - order.amount_paid > 0 && (
              <div className="flex items-center justify-between text-sm mt-1">
                <span className="text-muted-foreground">
                  {lang === 'ru' ? 'К оплате' : 'Balance Due'}
                </span>
                <span className="text-red-600 font-medium">
                  {formatCurrency(order.total_amount - order.amount_paid)}
                </span>
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Main Client Portal Page
export default function ClientPortalPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [portalAssets, setPortalAssets] = useState([]);
  const [activeTab, setActiveTab] = useState('orders');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const loadPortalData = useCallback(async () => {
    if (!token) {
      setError('no_token');
      setLoading(false);
      return;
    }

    try {
      // Validate token and get client info
      const validateRes = await axios.get(`${API_URL}/api/portal/validate?token=${token}`);
      setClientData(validateRes.data.client);

      // Load summary
      const summaryRes = await axios.get(`${API_URL}/api/portal/summary?token=${token}`);
      setSummary(summaryRes.data);

      // Load orders
      const ordersRes = await axios.get(`${API_URL}/api/portal/orders?token=${token}`);
      setOrders(ordersRes.data);

      // Load payments
      const paymentsRes = await axios.get(`${API_URL}/api/portal/payments?token=${token}`);
      setPayments(paymentsRes.data);

      // Load visible assets (documents)
      try {
        const clientId = validateRes.data.client?.id;
        if (clientId) {
          const assetsRes = await axios.get(`${API_URL}/api/portal/client/${clientId}/assets?token=${token}`);
          setPortalAssets(assetsRes.data || []);
        }
      } catch (e) { console.error('Portal assets load error:', e); }

    } catch (err) {
      console.error('Portal load error:', err);
      setError('invalid_token');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadPortalData();
  }, [loadPortalData]);

  // SEC-01 FIX: Secure file download for portal (uses portal token, not JWT)
  const handlePortalDownload = async (fileId) => {
    if (!clientData?.id || !token) return;
    try {
      const response = await axios.get(
        `${API_URL}/api/portal/client/${clientData.id}/files/${fileId}/download?token=${token}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] || 'application/pdf' 
      });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error('Portal download error:', err);
      alert(lang === 'ru' ? 'Ошибка загрузки файла' : 'Error downloading file');
    }
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setDetailOpen(true);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  // Error state
  if (error) {
    return <InvalidToken lang={lang} />;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
              <Home className="h-5 w-5 text-accent" />
            </div>
            <div>
              <h1 className="font-semibold text-lg">{lang === 'ru' ? 'Клиентский портал' : 'Client Portal'}</h1>
              <p className="text-sm text-muted-foreground">{clientData?.name}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <Briefcase className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{summary.orders?.total || 0}</p>
                    <p className="text-sm text-muted-foreground">
                      {lang === 'ru' ? 'Всего заказов' : 'Total Orders'}
                    </p>
                  </div>
                </div>
                <Separator className="my-4" />
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {lang === 'ru' ? 'Выполнено' : 'Completed'}
                  </span>
                  <span className="font-medium text-green-600">{summary.orders?.completed || 0}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-muted-foreground">
                    {lang === 'ru' ? 'Предстоящие' : 'Upcoming'}
                  </span>
                  <span className="font-medium text-blue-600">{summary.orders?.upcoming || 0}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <DollarSign className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{formatCurrency(summary.payments?.total_paid || 0)}</p>
                    <p className="text-sm text-muted-foreground">
                      {lang === 'ru' ? 'Оплачено' : 'Total Paid'}
                    </p>
                  </div>
                </div>
                <Separator className="my-4" />
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {lang === 'ru' ? 'Всего начислено' : 'Total Billed'}
                  </span>
                  <span className="font-medium">{formatCurrency(summary.payments?.total_billed || 0)}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className={cn(
                    "w-12 h-12 rounded-lg flex items-center justify-center",
                    (summary.payments?.balance_due || 0) > 0 
                      ? "bg-red-100 dark:bg-red-900/30" 
                      : "bg-green-100 dark:bg-green-900/30"
                  )}>
                    <CreditCard className={cn(
                      "h-6 w-6",
                      (summary.payments?.balance_due || 0) > 0 ? "text-red-600" : "text-green-600"
                    )} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{formatCurrency(summary.payments?.balance_due || 0)}</p>
                    <p className="text-sm text-muted-foreground">
                      {lang === 'ru' ? 'К оплате' : 'Balance Due'}
                    </p>
                  </div>
                </div>
                {(summary.payments?.balance_due || 0) > 0 && (
                  <>
                    <Separator className="my-4" />
                    <p className="text-sm text-muted-foreground">
                      {lang === 'ru' 
                        ? 'Свяжитесь с нами для оплаты'
                        : 'Contact us to arrange payment'}
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b pb-2">
          <Button
            variant={activeTab === 'orders' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('orders')}
            data-testid="portal-tab-orders"
          >
            <Briefcase className="h-4 w-4 mr-2" />
            {lang === 'ru' ? 'Заказы' : 'Orders'}
          </Button>
          <Button
            variant={activeTab === 'payments' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('payments')}
            data-testid="portal-tab-payments"
          >
            <CreditCard className="h-4 w-4 mr-2" />
            {lang === 'ru' ? 'Платежи' : 'Payments'}
          </Button>
          <Button
            variant={activeTab === 'documents' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('documents')}
            data-testid="portal-tab-documents"
          >
            <FileText className="h-4 w-4 mr-2" />
            {lang === 'ru' ? 'Документы' : 'Documents'}
          </Button>
        </div>

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5" />
                {lang === 'ru' ? 'Ваши заказы' : 'Your Orders'}
              </CardTitle>
              <CardDescription>
                {lang === 'ru' ? 'История всех ваших заказов' : 'History of all your service orders'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {orders.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Briefcase className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>{lang === 'ru' ? 'Нет заказов' : 'No orders yet'}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{lang === 'ru' ? 'Заказ' : 'Order'}</TableHead>
                      <TableHead>{lang === 'ru' ? 'Дата' : 'Date'}</TableHead>
                      <TableHead>{lang === 'ru' ? 'Статус' : 'Status'}</TableHead>
                      <TableHead className="text-right">{lang === 'ru' ? 'Сумма' : 'Amount'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((order) => {
                      const opStatus = OPERATIONAL_STATUSES[order.operational_status];
                      const payStatus = PAYMENT_STATUSES[order.payment_status];
                      const StatusIcon = opStatus?.icon || Clock;
                      
                      return (
                        <TableRow 
                          key={order.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => handleViewOrder(order)}
                          data-testid={`portal-order-${order.id}`}
                        >
                          <TableCell>
                            <div>
                              <p className="font-medium">{order.title}</p>
                              {order.service_type_name && (
                                <p className="text-sm text-muted-foreground">{order.service_type_name}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{formatDate(order.scheduled_date)}</p>
                              {order.scheduled_time_start && (
                                <p className="text-sm text-muted-foreground">{order.scheduled_time_start}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              <Badge className={cn("w-fit flex items-center gap-1", opStatus?.color)}>
                                <StatusIcon className="h-3 w-3" />
                                {lang === 'ru' ? opStatus?.labelRu : opStatus?.label}
                              </Badge>
                              <Badge className={cn("w-fit", payStatus?.color)}>
                                {lang === 'ru' ? payStatus?.labelRu : payStatus?.label}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="font-medium tabular-nums">
                              {formatCurrency(order.total_amount)}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                {lang === 'ru' ? 'История платежей' : 'Payment History'}
              </CardTitle>
              <CardDescription>
                {lang === 'ru' ? 'Все ваши платежи' : 'All your payments'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {payments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CreditCard className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>{lang === 'ru' ? 'Нет платежей' : 'No payments yet'}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{lang === 'ru' ? 'Дата' : 'Date'}</TableHead>
                      <TableHead>{lang === 'ru' ? 'Заказ' : 'Order'}</TableHead>
                      <TableHead>{lang === 'ru' ? 'Статус' : 'Status'}</TableHead>
                      <TableHead className="text-right">{lang === 'ru' ? 'Сумма' : 'Amount'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow key={payment.id} data-testid={`portal-payment-${payment.id}`}>
                        <TableCell>{formatDate(payment.created_at)}</TableCell>
                        <TableCell>{payment.order_title}</TableCell>
                        <TableCell>
                          <Badge className={cn(
                            payment.status === 'succeeded' ? 'bg-green-100 text-green-700' :
                            payment.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          )}>
                            {payment.status === 'succeeded' ? (lang === 'ru' ? 'Оплачен' : 'Paid') :
                             payment.status === 'pending' ? (lang === 'ru' ? 'Ожидает' : 'Pending') :
                             (lang === 'ru' ? 'Ошибка' : 'Failed')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          {formatCurrency(payment.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        {/* Documents Tab (Portal) */}
        {activeTab === 'documents' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {lang === 'ru' ? 'Ваши документы' : 'Your Documents'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {portalAssets.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  <p>{lang === 'ru' ? 'Нет доступных документов' : 'No documents available'}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {portalAssets.map(asset => {
                    const typeLabels = { contract: 'Договор', signed_contract: 'Подписан', invoice: 'Счёт', receipt: 'Квитанция', payment_proof: 'Чек оплаты', estimate: 'Смета', other: 'Документ' };
                    const typeLabelsEn = { contract: 'Contract', signed_contract: 'Signed', invoice: 'Invoice', receipt: 'Receipt', payment_proof: 'Payment proof', estimate: 'Estimate', other: 'Document' };
                    return (
                      <div key={asset.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`portal-asset-${asset.id}`}>
                        <div>
                          <p className="font-medium text-sm">{asset.title || asset.filename || 'Документ'}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="secondary" className="text-xs">{lang === 'ru' ? typeLabels[asset.asset_type] || asset.asset_type : typeLabelsEn[asset.asset_type] || asset.asset_type}</Badge>
                            {asset.created_at && <span className="text-xs text-muted-foreground">{new Date(asset.created_at).toLocaleDateString(lang === 'ru' ? 'ru' : 'en')}</span>}
                          </div>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => handlePortalDownload(asset.file_id)} data-testid={`portal-asset-open-${asset.id}`}>
                          {lang === 'ru' ? 'Открыть' : 'Open'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Contact Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              {lang === 'ru' ? 'Ваша контактная информация' : 'Your Contact Information'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {clientData?.email && (
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span>{clientData.email}</span>
                </div>
              )}
              {clientData?.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span>{clientData.phone}</span>
                </div>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              {lang === 'ru' 
                ? 'Если информация неверна, свяжитесь с нами для обновления.'
                : 'If any information is incorrect, please contact us to update it.'}
            </p>
          </CardContent>
        </Card>
      </main>

      {/* Order Detail Sheet */}
      <OrderDetailSheet
        order={selectedOrder}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        lang={lang}
      />
    </div>
  );
}
